// Capacitor plugin proxies (Haptics, Preferences, Share, Browser, App,
// SecureStorage) are safe to import statically even though this module is
// pulled into every bundle path via `index.ts` (including the Telegram
// bundle): `registerPlugin` builds a lazy proxy object at *import* time and
// only dispatches to `Capacitor.isNativePlatform()` / the web fallback per
// *call* — it does not perform native calls or throw on import. Only
// invoking a method outside a native shell can reject, which every wrapper
// below already guards against with `.catch(() => {})` or a try/catch.
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";
import { Preferences } from "@capacitor/preferences";
import { Share } from "@capacitor/share";
import { Browser } from "@capacitor/browser";
import { App as CapacitorApp } from "@capacitor/app";
import { PushNotifications } from "@capacitor/push-notifications";
import { SecureStorage } from "@aparajita/capacitor-secure-storage";
import { StatusBar, Style } from "@capacitor/status-bar";
import type { AsyncKeyValueStorage, HapticApi } from "@/lib/telegram";
import {
  APPSTORE_PRODUCT_IDS,
  isAppStoreProductCode,
  verifyAppStorePurchase,
} from "@/api/appstore";
import { queryClient } from "@/api/queryClient";
import { ACTIONS, FEATURES, track } from "@/lib/analytics";
import { resolveDeepLinkUrl } from "@/lib/deeplink";
import { navigateTo } from "@/lib/navigation";
import type { AppearanceApi, Theme } from "@/lib/theme";
import type {
  BackButtonApi,
  BuyOutcome,
  MainButtonApi,
  Platform,
  PlatformProduct,
  PurchasesApi,
  RestoreOutcome,
  SharePayload,
} from "./types";
import { useIosUiStore } from "./iosUiStore";
import { decodeJwsTransactionId, StoreKit } from "./storekit";

const iosHaptics: HapticApi = {
  impact: (style) => {
    // Telegram's HapticApi has two styles (`rigid`, `soft`) Capacitor's
    // ImpactStyle doesn't — both map to Medium.
    const capacitorStyle =
      style === "light" ? ImpactStyle.Light : style === "heavy" ? ImpactStyle.Heavy : ImpactStyle.Medium;
    Haptics.impact({ style: capacitorStyle }).catch(() => {});
  },
  notification: (type) => {
    const capacitorType =
      type === "success"
        ? NotificationType.Success
        : type === "warning"
          ? NotificationType.Warning
          : NotificationType.Error;
    Haptics.notification({ type: capacitorType }).catch(() => {});
  },
  selectionChanged: () => {
    Haptics.selectionChanged().catch(() => {});
  },
};

const storage: AsyncKeyValueStorage = {
  getItem: (key) => Preferences.get({ key }).then((result) => result.value),
  setItem: (key, value) => Preferences.set({ key, value }),
  removeItem: (key) => Preferences.remove({ key }),
  getKeys: () => Preferences.keys().then((result) => result.keys),
};

// Keychain-backed. Values are always written as plain strings, so a
// non-string DataType coming back from `get()` (only possible if something
// else wrote a Date/object under this key) is treated as absent rather than
// surfaced as a type error to callers of AsyncKeyValueStorage.
const secureStorage: AsyncKeyValueStorage = {
  getItem: (key) =>
    SecureStorage.get(key).then((value) => (typeof value === "string" ? value : null)),
  setItem: (key, value) => SecureStorage.set(key, value),
  removeItem: (key) => SecureStorage.remove(key).then(() => undefined),
  getKeys: () => SecureStorage.keys(),
};

const backButton: BackButtonApi = {
  show(onClick) {
    return useIosUiStore.getState().showBack(onClick);
  },
};

const mainButton: MainButtonApi = {
  set(state) {
    return useIosUiStore.getState().setMain(state);
  },
};

// Capacitor's naming is inverted relative to what it does: `Style.Dark`
// asks for LIGHT status-bar content (for a dark background) and
// `Style.Light` asks for dark content. So the light ground — dawn — wants
// Style.Light. `setBackgroundColor` is Android-only and deliberately not
// called. Fire-and-forget with a `.catch`, like the haptics wrappers above:
// outside a native shell the plugin rejects with "not implemented on web".
const appearance: AppearanceApi = {
  apply(theme: Theme): void {
    StatusBar.setStyle({ style: theme === "dawn" ? Style.Light : Style.Dark }).catch(() => {});
  },
};

async function share(payload: SharePayload): Promise<void> {
  try {
    // Capacitor Share on iOS attaches URLs (image or link) to the native
    // share sheet, which previews them itself — sharing the image URL
    // directly is sufficient for v1.
    await Share.share({ text: payload.text, url: payload.imageUrl ?? payload.url });
  } catch {
    // User cancelled the native share sheet, or sharing isn't supported.
  }
}

function openLink(url: string): void {
  // DUE-195: `Browser.open` is SFSafariViewController — it only renders
  // web content, so an App Store deep link (`itms-apps://apps.apple.com/
  // account/subscriptions`) handed to it goes nowhere. Any non-http(s)
  // scheme is instead driven as a top-level navigation: Capacitor's
  // `WebViewDelegationHandler.decidePolicyFor` cancels navigations that
  // leave the app bundle and forwards them to `UIApplication.shared.open`,
  // which is exactly the system handler such schemes need. No `allowNavigation`
  // hosts are configured (`capacitor.config.ts`), so nothing short-circuits
  // that path.
  if (!/^https?:$/i.test(schemeOf(url))) {
    window.location.href = url;
    return;
  }
  // SFSafariViewController; fire-and-forget like Telegram's tg.openLink.
  Browser.open({ url }).catch(() => {});
}

/** `"https:"` for an absolute URL, `""` for anything relative/unparseable
 * (which then falls through to the in-app browser, the old behaviour). */
function schemeOf(url: string): string {
  const match = /^([a-z][a-z0-9+.-]*:)/i.exec(url.trim());
  return match?.[1] ?? "https:";
}

// DUE-161 task 5: `purchases` bridges the native StoreKit plugin
// (`./storekit.ts`, implemented natively in task 7) to the existing
// payment/credit-ledger machinery via `POST /api/payments/appstore/verify`
// (`api/appstore.ts`).
//
// Cache invalidation mirrors `App.tsx`'s own belt-and-suspenders
// `["me"]` invalidation on app focus: partial query-key matching means
// `["me"]` covers `ME_QUERY_KEY` (`["me", "profile"]`) and `["subscriptions"]`
// covers `SUBSCRIPTION_ME_QUERY_KEY` (`["subscriptions", "me"]`) — the two
// query families PaywallPage/ProfilePage read credits and subscription
// state from. `SUB_MONTH`/`TRANSIT_MONTH`/`ANNUAL` grant subscription time;
// `PAIR_1` grants credits — both live under `/api/me`.
function invalidatePurchaseCaches(): void {
  void queryClient.invalidateQueries({ queryKey: ["me"] });
  void queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
}

async function listProducts(codes: string[]): Promise<PlatformProduct[]> {
  const productIds = codes
    .filter(isAppStoreProductCode)
    .map((code) => APPSTORE_PRODUCT_IDS[code]);
  const { products } =
    productIds.length > 0 ? await StoreKit.getProducts({ ids: productIds }) : { products: [] };
  const byId = new Map(products.map((p) => [p.id, p]));
  return codes.map((code) => {
    const productId = isAppStoreProductCode(code) ? APPSTORE_PRODUCT_IDS[code] : undefined;
    const info = productId ? byId.get(productId) : undefined;
    return {
      code,
      productId,
      displayPrice: info?.displayPrice,
      displayName: info?.displayName,
    };
  });
}

// Verifies a JWS with the backend and, on success, finishes the matching
// StoreKit transaction. Shared by `buy()` (a fresh purchase) and the
// `transactionUpdated` listener wired in `createIosPlatform()` below
// (renewals / Ask-to-Buy approvals / transactions from another device, via
// `Transaction.updates`) — both need the exact same verify -> invalidate ->
// finish sequence. Returns whether verification succeeded; callers needing
// paid/cancelled/failed semantics (`buy()`) build on top of this rather than
// duplicating it.
async function verifyAndFinish(jws: string): Promise<boolean> {
  try {
    await verifyAppStorePurchase(jws);
  } catch {
    // Verification rejected (bad bundle id, unknown product, tampered
    // JWS) or the request itself failed — do NOT finish the transaction:
    // an unfinished transaction re-delivers via currentEntitlements() /
    // Transaction.updates so it can be retried instead of silently lost.
    return false;
  }
  invalidatePurchaseCaches();
  const transactionId = decodeJwsTransactionId(jws);
  if (transactionId) {
    await StoreKit.finishTransaction({ transactionId });
  }
  return true;
}

async function buy(code: string): Promise<BuyOutcome> {
  if (!isAppStoreProductCode(code)) {
    // Not a reachable path from real callers (every PaymentProductCode the
    // paywall offers on iOS is in APPSTORE_PRODUCT_IDS) — fail loud rather
    // than silently swallowing a caller bug, same posture as the
    // decode/verify failures below being handled, not this one.
    throw new Error(`storekit: no App Store product id mapped for code "${code}"`);
  }
  const result = await StoreKit.purchase({ productId: APPSTORE_PRODUCT_IDS[code] });
  if (result.cancelled || !result.jws) {
    // No error surfaced for a user cancel — mirrors useBuyProduct's
    // "cancelled" outcome on Telegram.
    return { status: "cancelled" };
  }
  const succeeded = await verifyAndFinish(result.jws);
  return { status: succeeded ? "paid" : "failed" };
}

async function restore(): Promise<RestoreOutcome> {
  const { transactions } = await StoreKit.currentEntitlements();
  let restored = 0;
  let anyActive = false;
  for (const { jws } of transactions) {
    try {
      // Replays are idempotent server-side (charge_id=transactionId
      // unique) — re-verifying an already-fulfilled entitlement just
      // confirms it's still valid, without re-granting.
      //
      // DUE-470 (was DUE-162 item 4): `restore: true` tells the server this
      // is a Restore, not a purchase. A live auto-renewable subscription
      // first bought by another guest (new device, same Apple ID) is then
      // moved to THIS account (`transferred: true`), so it counts as
      // restored like a fresh grant. If nothing was restored but an
      // entitlement is already active here (`active: true`), the UI says
      // the subscription is active instead of "Nothing to restore".
      const { granted, transferred, active } = await verifyAppStorePurchase(jws, {
        restore: true,
      });
      if (granted || transferred) restored += 1;
      else if (active) anyActive = true;
    } catch {
      // Skip entitlements that fail verification (stale/tampered/etc.) —
      // one bad entry must not abort restoring the rest.
    }
  }
  if (restored > 0) invalidatePurchaseCaches();
  if (restored === 0 && anyActive) return { restored, active: true };
  return { restored };
}

const purchases: PurchasesApi = { listProducts, buy, restore };

export interface CreateIosPlatformDeps {
  /** Test seam for close(); defaults to a real navigation. */
  navigateHome?: () => void;
}

export function createIosPlatform(deps: CreateIosPlatformDeps = {}): Platform {
  const navigateHome = deps.navigateHome ?? ((): void => window.location.assign("/"));

  // Mirrors StoreKitPlugin.swift's `Transaction.updates` listener, started
  // once when the native plugin loads: verify + finish any transaction that
  // arrives outside a `buy()` call (renewals, Ask-to-Buy approvals, a
  // purchase made on another device). Fire-and-forget, `.catch()`-guarded
  // like the haptics/share calls below — outside a native shell (web
  // fallback, tests that don't mock `../storekit`) the plugin call rejects
  // with "not implemented on web", which must not become an unhandled
  // rejection or block adapter init.
  StoreKit.addListener("transactionUpdated", (event) => {
    // verifyAndFinish catches verify errors internally, but its
    // finishTransaction await can still reject — swallow it here (the
    // unfinished transaction re-delivers later). buy() intentionally does
    // not get this guard: there the caller awaits and surfaces errors.
    verifyAndFinish(event.jws).catch(() => {});
  }).catch(() => {});

  // Subproject D task 4: `duetto://` push-payload/internal links and
  // `https://app.theduetto.com/...` share links (opened via Safari today,
  // natively once Universal Links land in subproject E) both surface here.
  // `resolveDeepLinkUrl` is pure — a null result (unknown host/path, or a
  // `start` param that itself doesn't resolve) means "do nothing", not an
  // error. Same `.catch(() => {})` posture as the StoreKit listener above:
  // outside a native shell `addListener` rejects and must not block init.
  CapacitorApp.addListener("appUrlOpen", (event) => {
    const route = resolveDeepLinkUrl(event.url);
    if (route) {
      navigateTo(route);
    }
  }).catch(() => {});

  // A tap on a delivered APNs alert does NOT fire `appUrlOpen` — iOS
  // surfaces it only through the push plugin's action event. The daily
  // transit payload carries `deep_link: duetto://transits/{date}`
  // (src/core/apns/payload.py); route it through the same resolver so
  // both entry points share one allow-list. Cold start is covered by the
  // pending-route queue in lib/navigation.ts.
  PushNotifications.addListener("pushNotificationActionPerformed", (event) => {
    // DUE-213: every tap on a delivered push IS the user opening it —
    // fire before the deep-link resolution below, which is a routing
    // detail and must not gate whether the open gets counted (a stale
    // or malformed deep_link would otherwise silently undercount opens).
    track(FEATURES.push, ACTIONS.opened);
    const deepLink = event.notification.data?.deep_link;
    if (typeof deepLink !== "string") {
      return;
    }
    const route = resolveDeepLinkUrl(deepLink);
    if (route) {
      navigateTo(route);
    }
  }).catch(() => {});

  return {
    name: "ios",
    haptics: iosHaptics,
    storage,
    secureStorage,
    backButton,
    mainButton,
    appearance,
    share,
    openLink,
    // iOS apps don't self-close; "done" semantics = navigate back to home.
    close(): void {
      navigateHome();
    },
    supportsPurchases: true,
    purchases,
  };
}
